import { AdMob, BannerAdPosition, BannerAdSize, BannerAdOptions, AdOptions, RewardAdOptions } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

/**
 * AdService handles all advertisement logic.
 * It uses @capacitor-community/admob for stable Banner, Interstitial, and Rewarded Ads.
 */

// Real Google AdMob IDs
const AD_UNITS = {
    ANDROID: {
        BANNER: 'ca-app-pub-7021783816155246/9885177265',
        INTERSTITIAL: 'ca-app-pub-7021783816155246/3091558264',
        REWARDED: 'ca-app-pub-7021783816155246/3155612807',
        NATIVE: 'ca-app-pub-7021783816155246/7395367414',
    },
    IOS: {
        BANNER: 'ca-app-pub-3940256099942544/2934735716',
        INTERSTITIAL: 'ca-app-pub-3940256099942544/4411468910',
        REWARDED: 'ca-app-pub-3940256099942544/1712485313',
        NATIVE: 'ca-app-pub-3940256099942544/3986624511',
    }
};

const getAdUnitId = (type: 'BANNER' | 'INTERSTITIAL' | 'REWARDED' | 'NATIVE') => {
    const isIOS = Capacitor.getPlatform() === 'ios';
    
    if (isIOS) {
        if (type === 'BANNER') return import.meta.env.VITE_ADMOB_IOS_BANNER || AD_UNITS.IOS.BANNER;
        if (type === 'INTERSTITIAL') return import.meta.env.VITE_ADMOB_IOS_INTERSTITIAL || AD_UNITS.IOS.INTERSTITIAL;
        if (type === 'REWARDED') return import.meta.env.VITE_ADMOB_IOS_REWARDED || AD_UNITS.IOS.REWARDED;
        if (type === 'NATIVE') return import.meta.env.VITE_ADMOB_IOS_NATIVE || AD_UNITS.IOS.NATIVE;
        return AD_UNITS.IOS[type];
    } else {
        if (type === 'BANNER') return import.meta.env.VITE_ADMOB_ANDROID_BANNER || AD_UNITS.ANDROID.BANNER;
        if (type === 'INTERSTITIAL') return import.meta.env.VITE_ADMOB_ANDROID_INTERSTITIAL || AD_UNITS.ANDROID.INTERSTITIAL;
        if (type === 'REWARDED') return import.meta.env.VITE_ADMOB_ANDROID_REWARDED || AD_UNITS.ANDROID.REWARDED;
        if (type === 'NATIVE') return import.meta.env.VITE_ADMOB_ANDROID_NATIVE || AD_UNITS.ANDROID.NATIVE;
        return AD_UNITS.ANDROID[type];
    }
};

let isBannerShowing = false;
let isBannerCreated = false;
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
let debugMode = false;
let clickCount = 0;
let lastBannerFailureTime = 0;

const log = (msg: string, data?: any) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[AdService ${timestamp}] ${msg}`, data || '');
};

// Web placeholders for ads
export const _adWebHooks = {
    onShowInterstitial: null as (() => void) | null,
    onShowRewarded: null as ((onReward: () => void) => void) | null,
};

const initialize = async (): Promise<void> => {
    const adsEnabled = import.meta.env.VITE_ENABLE_ADS !== 'false';
    if (!adsEnabled) return;

    if (Capacitor.isNativePlatform()) {
        if (isInitialized) return;
        if (initializationPromise) return initializationPromise;

        initializationPromise = (async () => {
            try {
                try {
                    await AdMob.requestTrackingAuthorization();
                    log('ATT Tracking Authorization requested successfully');
                } catch (attError) {
                    log('ATT request skipped or failed (not iOS or already requested)', attError);
                }

                await AdMob.initialize({
                    testingDevices: [],
                    initializeForTesting: false,
                });
                
                // Robust App State Management
                CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
                    log(`App State Changed: isActive=${isActive}`);
                    if (isActive) {
                        // Small delay to ensure the native view is ready after resume
                        setTimeout(async () => {
                            // We don't check isBannerShowing here anymore. 
                            // Instead, we rely on the UI layer (App.tsx) to tell us if we should show.
                            // But as a fallback, we can try to restore the last known state.
                            log('App Resumed: Checking if banner should be re-shown...');
                            document.dispatchEvent(new CustomEvent('app-resumed'));
                        }, 1000); 
                    }
                });

                // Listen for banner events to track actual visibility
                AdMob.addListener('bannerAdLoaded' as any, () => {
    log('Banner Ad Loaded successfully');
    // We only mark as showing if it was actually requested to be shown
    // This prevents race conditions where an ad loads right after we hide it
});

AdMob.addListener('bannerAdOpened' as any, () => {
    log('Banner Ad Opened/Impression');
});

AdMob.addListener('bannerAdFailedToLoad' as any, (info: any) => {
    log('Banner Ad Failed to Load', info);
    isBannerShowing = false;
});

                // Periodic Resurrection check removed
                isInitialized = true;
                log('AdMob Initialized successfully');
            } catch (e) {
                console.error('AdMob Initialization failed:', e);
            }
        })();

        return initializationPromise;
    }
};

const ensureInitialized = async (): Promise<boolean> => {
    if (isInitialized) return true;
    if (initializationPromise) {
        await initializationPromise;
        return isInitialized;
    }
    await initialize();
    return isInitialized;
};

const showInterstitialAd = async (): Promise<void> => {
    if (import.meta.env.VITE_ENABLE_ADS === 'false') return;

    if (Capacitor.isNativePlatform()) {
        const ready = await ensureInitialized();
        if (!ready) return;

        try {
            log('Preparing Interstitial Ad...');
            const options: AdOptions = {
                adId: getAdUnitId('INTERSTITIAL'),
                isTesting: Capacitor.getPlatform() === 'ios' || import.meta.env.VITE_ENABLE_ADS_TESTING === 'true'
            };
            await AdMob.prepareInterstitial(options);
            await AdMob.showInterstitial();
            log('Interstitial Ad shown');
        } catch (e) {
            console.error('AdMob Interstitial failed', e);
        }
    } else {
        // Web Placeholder
        log('Showing Web Interstitial Placeholder');
        if (_adWebHooks.onShowInterstitial) {
            _adWebHooks.onShowInterstitial();
        }
    }
};

const showRewardedAd = async (onReward: () => void): Promise<void> => {
    if (import.meta.env.VITE_ENABLE_ADS === 'false') {
        onReward();
        return;
    }

    if (Capacitor.isNativePlatform()) {
        const ready = await ensureInitialized();
        if (!ready) {
            onReward();
            return;
        }

        try {
            log('Preparing Rewarded Ad...');
            const options: RewardAdOptions = {
                adId: getAdUnitId('REWARDED'),
                isTesting: Capacitor.getPlatform() === 'ios' || import.meta.env.VITE_ENABLE_ADS_TESTING === 'true'
            };
            
            let rewardEarned = false;

            // Listen for the reward
            const rewardListener = await AdMob.addListener('onRewardedVideoAdReward' as any, () => {
                log('Reward earned!');
                rewardEarned = true;
            });

            // Listen for dismissal before triggering the callback
            const dismissListener = await AdMob.addListener('onRewardedVideoAdDismissed' as any, () => {
                log('Rewarded Ad dismissed');
                if (rewardEarned) {
                    // Small delay to ensure native ad view is completely gone before any alerts fire
                    setTimeout(() => {
                        onReward();
                    }, 300);
                }
                rewardListener.remove();
                dismissListener.remove();
            });

            await AdMob.prepareRewardVideoAd(options);
            await AdMob.showRewardVideoAd();
            log('Rewarded Ad shown');
        } catch (e) {
            console.error('AdMob Rewarded Video failed', e);
            onReward();
        }
    } else {
        // Web Placeholder
        log('Showing Web Rewarded Placeholder');
        if (_adWebHooks.onShowRewarded) {
            _adWebHooks.onShowRewarded(onReward);
        } else {
            onReward();
        }
    }
};

const showBanner = async (force: boolean = false): Promise<void> => {
    if (import.meta.env.VITE_ENABLE_ADS === 'false') return;
    if (!Capacitor.isNativePlatform()) return;

    if (Date.now() - lastBannerFailureTime < 30000 && !force) {
        log('Skipping banner try due to recent failure (throttle)');
        return;
    }

    try {
        const ready = await ensureInitialized();
        if (!ready) return;

        // If it's already thought to be showing and we're not forcing, just resume
        if (isBannerShowing && !force) {
            log('Banner already showing, ensuring active with resumeBanner');
            AdMob.resumeBanner().catch(() => {});
            return;
        }

        if (isBannerCreated) {
            log('Banner already created, resuming...');
            await AdMob.resumeBanner();
            isBannerShowing = true;
            lastBannerFailureTime = 0;
            return;
        }

        log('Attempting to show banner...');
        const options: BannerAdOptions = {
            adId: getAdUnitId('BANNER'),
            adSize: BannerAdSize.BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
            margin: 0, 
            isTesting: Capacitor.getPlatform() === 'ios' || import.meta.env.VITE_ENABLE_ADS_TESTING === 'true'
        };

        // Every time we want to SHOW, we call showBanner.
        // This is the most reliable way to ensure visibility after a hideBanner call.
        await AdMob.showBanner(options);
        isBannerShowing = true;
        isBannerCreated = true;
        lastBannerFailureTime = 0;
        log('Banner show command sent successfully');
    } catch (e) {
        log('AdMob Banner failed', e);
        isBannerShowing = false;
        lastBannerFailureTime = Date.now();
        // If showBanner failed, we might need a full recreation next time
        isBannerCreated = false;
    }
};

const hideBanner = async (force: boolean = false): Promise<void> => {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
        const ready = await ensureInitialized();
        if (!ready) return;
        
        // We always try to hide if it's showing OR if forced
        if (!isBannerShowing && !force) return;

        log('Hiding banner...');
        await AdMob.hideBanner();
        isBannerShowing = false;
        log('Banner hidden successfully');
    } catch (e) {
        log('AdMob Hide Banner failed', e);
        // On error, assume it's still hidden or in a bad state
        isBannerShowing = false;
    }
};

const showConsentForm = async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
        const ready = await ensureInitialized();
        if (!ready) return;

        await AdMob.showConsentForm();
    } catch (e) {
        console.error('AdMob Show Consent Form failed', e);
    }
};

const showNativeAd = async (elementId: string): Promise<void> => {
    // Native Ads are not supported by @capacitor-community/admob.
    // We log this for debugging purposes.
    console.log('AdService: showNativeAd called for', elementId);
};

const loadNativeAd = async (): Promise<any> => {
    // Mock for web
    return {
        headline: 'Cosmic Coffee',
        body: 'The best coffee in the galaxy. Try our new Nebula Nectar!',
        image: { url: 'https://images.unsplash.com/photo-1559496417-e7f25cb247f3?q=80&w=1887&auto=format&fit=crop' },
        callToAction: 'Order Now',
        starRating: 4.5,
        advertiser: 'Cosmic Coffee Co.',
    };
};

const registerClick = () => {
    clickCount++;
    log(`Click registered. Total clicks: ${clickCount}`);
    if (clickCount % 5 === 0) {
        showInterstitialAd();
    }
};

export const AdService = {
    initialize,
    showInterstitialAd,
    showRewardedAd,
    showBanner,
    hideBanner,
    showConsentForm,
    loadNativeAd,
    showNativeAd,
    registerClick,
    getBannerStatus: () => isBannerShowing,
    setDebug: (val: boolean) => { debugMode = val; log(`Debug mode set to ${val}`); },
    testFeedAd: () => {
        log('Testing Feed Ad integration...');
        alert('Feed Ads (Native Ads) are integrated into the Home Feed every 3 posts. Scroll down the Home Feed to see the "Sponsored Content" placeholders.');
    }
};

